import { SubscriptionIntegration } from "@redotech/redo-model/subscription-integrations/subscription-integration";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useEditSubscriptionAddress } from "./use-subscription-queries";

interface EditAddressModalProps {
  subscription: SubscriptionIntegration;
  customerEmail: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export const EditAddressModal = memo(function EditAddressModal({
  subscription,
  customerEmail,
  isOpen,
  onClose,
}: EditAddressModalProps) {
  const editAddress = useEditSubscriptionAddress(customerEmail);

  const [address1, setAddress1] = useState(
    subscription.address?.address1 || "",
  );
  const [address2, setAddress2] = useState(
    subscription.address?.address2 || "",
  );
  const [city, setCity] = useState(subscription.address?.city || "");
  const [province, setProvince] = useState(
    subscription.address?.province || "",
  );
  const [zip, setZip] = useState(subscription.address?.zip || "");
  const [country, setCountry] = useState(subscription.address?.country || "");

  const handleSubmit = async () => {
    try {
      if (!address1 || !city || !province || !zip || !country) {
        toast("Please fill in all required fields", { variant: "error" });
        return;
      }

      await editAddress.mutateAsync({
        subscriptionId: subscription.id,
        address: {
          address1,
          address2: address2 || undefined,
          city,
          province,
          zip,
          country,
        },
      });

      toast("Address updated successfully", { variant: "success" });
      onClose();
    } catch (error: any) {
      toast(`Failed to update address: ${error.message || error}`, {
        variant: "error",
      });
    }
  };

  const isSubmitting = editAddress.isPending;

  return (
    <RedoModal
      hideCloseButton={isSubmitting}
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      title="Edit Shipping Address"
    >
      <Flex dir="column" gap="lg" p="lg">
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            Address Line 1 *
          </Text>
          <RedoTextInput setValue={setAddress1} size="sm" value={address1} />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            Address Line 2
          </Text>
          <RedoTextInput setValue={setAddress2} size="sm" value={address2} />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            City *
          </Text>
          <RedoTextInput setValue={setCity} size="sm" value={city} />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            State/Province *
          </Text>
          <RedoTextInput setValue={setProvince} size="sm" value={province} />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            ZIP/Postal Code *
          </Text>
          <RedoTextInput setValue={setZip} size="sm" value={zip} />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            Country *
          </Text>
          <RedoTextInput setValue={setCountry} size="sm" value={country} />
        </Flex>

        <Flex gap="md" justify="flex-end">
          <RedoButton
            disabled={isSubmitting}
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
          />
          <RedoButton
            disabled={isSubmitting}
            hierarchy="primary"
            onClick={handleSubmit}
            size="md"
            text={isSubmitting ? "Saving..." : "Save Changes"}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
